import { actionHistory } from '../../store';
import type { Modifier } from './modifier';
import { ModifierTags, type ModifierParams } from './modifier.type';
import { StatusEffect } from './modifier.type';
import { StatusEffectRegistry } from './status-effects';
import { createItem } from './items';
import { createTrait } from './traits';
import type { Item, Items } from './item.type';
import type { Damage } from './damage.type';
import { type DieCreation, DieType } from './die.type';
import type { Trait } from './trait.type';
import { TraitType } from './trait-registry';
import { clone } from '../utils/utils';

export type Player = {
  title: string;
  text?: string;
  id: string;
  type?: string;
  archetype?: TraitType;
  healthMax: number;
  rerollMax: number;
  dice: Array<DieCreation>;
  colour: string;

  statusEffects: Array<Modifier>;
  traits: Array<Trait>;
  healthCurrent: number;

  level: number;
  experience: number;
  money: number;
  reputation: number;
  inventory: Array<Item>;

  takeDamage?: Function;
};

export type PlayerDefinition = {
  title: string;
  text: string;
  type: PlayerClass;
  healthMax: number;
  rerollMax?: number;
  colour?: string;
  dice?: Array<DieType>;
  dice1?: Array<DieType>;
  dice2?: Array<DieType>;
  dice3?: Array<DieType>;

  archetypes: Array<PlayerArchetype>;

  strengths?: Array<string>;
  weaknesses?: Array<string>;
};

export enum PlayerClass {
  Ranger = 'ranger',
  Fighter = 'fighter',
  Rogue = 'rogue',
  Elementalist = 'elementalist',
}

export enum PlayerComplexity {
  Simple = 'Simple',
  Moderate = 'Moderate',
  Complex = 'Complex',
}

export type PlayerArchetype = {
  title: string;
  text: string;
  id: TraitType;
  complexity: PlayerComplexity;
};

export function createPlayerInstance(type: PlayerClass, archetype?: TraitType) {
  let player = players.find((player) => {
    return player.type == type;
  });

  if (!player) {
    throw new Error(`No player with type: ${type}`);
  }

  var instance: Player = clone(player);
  instance.id = 'player';
  instance.type = type;
  instance.archetype = archetype;
  instance.healthCurrent = instance.healthMax;

  instance.level = 0;
  instance.experience = 0;
  instance.reputation = -5;
  instance.money = 10;

  instance.inventory = [];
  instance.statusEffects = [];
  instance.traits = [];

  /* var instance: Player = {
    title: player.title,
    text: player.text,
    id: 'player',
    type: type,
    archetype: archetype,
    healthMax: player.healthMax,
    rerollMax: player.rerollMax || 0,
    dice: [],
    colour: player.colour || '#FFFFFF',
    statusEffects: [],
    traits: [],
    healthCurrent: player.healthMax,
    level: 0,
    experience: 0,
    money: 10,
    reputation: -5,
    inventory: []
  } */

  if (archetype) {
    addTrait(instance, archetype);

    // Named for the archetype -- a Knight, not a Fighter -- everywhere the
    // player is shown, from the battle screens to the combat log.
    const definition = player.archetypes.find((a) => a.id == archetype);
    if (definition) {
      instance.title = definition.title;
    }
  }

  return instance;
}

export function clonePlayer(player: Player): Player {
  const newPlayer = {
    ...player,
  };

  newPlayer.dice = player.dice.map((die) => {
    return { ...die };
  });
  newPlayer.inventory = player.inventory.map((mod: Modifier) => {
    return mod.clone(newPlayer);
  });
  newPlayer.statusEffects = player.statusEffects.map((mod: Modifier) => {
    return mod.clone(newPlayer);
  });
  newPlayer.traits = player.traits.map((mod: Modifier) => {
    return mod.clone(newPlayer);
  });

  return newPlayer;
}

export function healthRemove(damage: Damage) {
  damage.target.healthCurrent -= damage.value;

  if (damage.target.takeDamage) {
    damage.target.takeDamage(damage);
  }
  if (damage.value > 0) {
    actionHistory.push(`${damage.value} pure damage to ${damage.target.title}`);
  }

  let event = new CustomEvent('createParticle', {
    detail: {
      type: 'text',
      animation: 'fall',
      value: damage.value,
      spawnTarget: '.portrait.' + damage.target.id,
      colour: 'red',
    },
  });
  window.dispatchEvent(event);

  return damage;
}

export function dealDamage(damage: Damage) {
  // Damage Out modifiers
  getModifiers(damage.source, 'damageOut').forEach((mod) => {
    if (mod.damageOut) {
      mod.damageOut(damage);
    }
  });

  // Damage In modifiers
  getModifiers(damage.target, 'damageIn').forEach((mod) => {
    if (mod.damageIn) {
      mod.damageIn(damage);
    }
  });

  damage.target.healthCurrent -= damage.value;
  if (damage.target.takeDamage) {
    damage.target.takeDamage(damage);
  }

  if (damage.value > 0) {
    actionHistory.push(`${damage.value} damage to ${damage.target.title}`);

    var event = new CustomEvent('createParticle', {
      detail: {
        type: 'text',
        animation: 'fall',
        value: damage.value,
        spawnTarget: '.portrait.' + damage.target.id,
        colour: 'red',
      },
    });
    window.dispatchEvent(event);
  }

  return damage;
}

export function healDamage(healing: Damage) {
  // Damage Out modifiers
  /*healing.source.statusEffects.filter(mod => 'healingOut' in mod)
  .forEach(mod => {
    mod.healingOut(healing);
  });*/

  // Healing In modifiers
  getModifiers(healing.target, 'healingIn').forEach((mod) => {
    if (mod.healingIn) {
      mod.healingIn(healing);
    }
  });

  let value = healing.value;
  if (healing.target.healthCurrent + healing.value > healing.target.healthMax) {
    value = healing.target.healthMax - healing.target.healthCurrent;
  }

  healing.target.healthCurrent += value;
  if (value > 0) {
    actionHistory.push(`${healing.target.title} healed ${value} health.`);
  }

  var event = new CustomEvent('createParticle', {
    detail: {
      type: 'text',
      animation: 'rise',
      value: value,
      spawnTarget: '.portrait.' + healing.target.id,
      colour: 'green',
    },
  });
  window.dispatchEvent(event);

  return healing;
}

export function addModifier(
  target: Player,
  type: StatusEffect,
  stacks: number,
  params?: ModifierParams,
) {
  getModifiers(target, 'modifierAdd').forEach((mod) => {
    if (mod.modifierAdd) {
      stacks = mod.modifierAdd(type, stacks, params);
    }
  });

  if (stacks > 0) {
    let modifier = target.statusEffects.find((modifier) => modifier.type == type);
    //if target has modifier
    if (modifier) {
      modifier.update(stacks, params);
    } else {
      modifier = StatusEffectRegistry.create(type, target, stacks, params);
      target.statusEffects.push(modifier);
      target.statusEffects = target.statusEffects.sort((a, b) => {
        return b.priority - a.priority;
      });
    }

    var event = new CustomEvent('createParticle', {
      detail: {
        type: 'icon',
        animation: 'grow',
        value: modifier.icon,
        spawnTarget: '.portrait.' + target.id,
      },
    });
    window.dispatchEvent(event);
  }
}

export function getModifiers(player: Player, type?: string): Array<Modifier> {
  const mods = player.statusEffects.concat(player.inventory).concat(player.traits);
  mods.sort((a, b) => {
    return a.priority - b.priority;
  });

  if (type) {
    return mods.filter((mod: Modifier) => type in mod);
  } else {
    return mods;
  }
}

export function addMoney(target: Player, value: number) {
  target.money += value;
}

export function hasItem(player: Player, type: Items) {
  return player.inventory.find((item) => item.type == type);
}
export function removeItem(player: Player, type: Items) {
  if (!hasItem(player, type)) {
    return;
  }

  const item = player.inventory.find((item) => item.type == type);
  if (item) {
    item.stacks--;
    if (item.stacks <= 0) {
      player.inventory.splice(player.inventory.indexOf(item), 1);
    }
  }
}
export function addItem(player: Player, type: Items) {
  if (hasItem(player, type)) {
    const item = player.inventory.find((item) => item.type == type);
    if (item) {
      item.stacks++;
    }
    return;
  }

  const item = createItem(type);
  item.owner = player;
  if (item) {
    player.inventory.push(item);
  }
}

export function hasTrait(player: Player, type: TraitType) {
  return player.traits.find((trait) => trait.type == type);
}
export function removeTrait(player: Player, type: TraitType) {
  if (!hasTrait(player, type)) {
    return;
  }

  const trait = player.traits.find((trait) => trait.type == type);
  if (trait) {
    player.traits.splice(player.traits.indexOf(trait), 1);
  }
}
export function addTrait(player: Player, type: TraitType) {
  if (hasTrait(player, type)) {
    return;
  }

  const trait = createTrait(player, type);
  if (trait) {
    player.traits.push(trait);
  }
}

export function changeReputation(player: Player, value: number) {
  player.reputation += value;
  if (player.reputation > 10) {
    player.reputation = 10;
  }
  if (player.reputation < -10) {
    player.reputation = -10;
  }
}

export const players: Array<PlayerDefinition> = [
  {
    title: 'Ranger',
    text: 'A ranger is a skilled archer and summoner who uses their expertise in wilderness survival and tracking, along with their trusty bow and summoned familiar to vanquish their enemies.',
    type: PlayerClass.Ranger,
    healthMax: 10,
    rerollMax: 2,
    dice1: [DieType.Bow, DieType.Shortsword, DieType.Crossbow, DieType.Blowgun],
    dice2: [DieType.Quiver, DieType.Bandolier, DieType.Case, DieType.AmmoBag],
    dice3: [DieType.Strength, DieType.Speed, DieType.Reflex, DieType.Rhythmic],
    archetypes: [
      {
        title: 'Archer',
        text: 'A master of the bow, excelling in precision and arrow management, delivering deadly volleys with unparalleled accuracy.',
        id: TraitType.Archer,
        complexity: PlayerComplexity.Moderate,
      },
      {
        title: 'Tamer',
        text: 'A skilled ranger with a loyal wolf companion, the Tamer dynamically evolves alongside their companion, unlocking diverse strategies and synergies in combat.',
        id: TraitType.Tamer,
        complexity: PlayerComplexity.Simple,
      },
      {
        title: 'Arbelist',
        text: 'A crossbow specialist, the Arbelist employs heavy bolts to deal devastating piercing damage, dominating foes with expert marksmanship.',
        id: TraitType.Arbelist,
        complexity: PlayerComplexity.Moderate,
      },
      {
        title: 'Piper',
        text: 'Wielding a blowpipe for both music and darts, the Piper mesmerizes enemies with enchanting tunes while tactically delivering poisonous projectiles from a distance.',
        id: TraitType.Piper,
        complexity: PlayerComplexity.Complex,
      },
    ],
    colour: '#E3C76D',
  },
  {
    title: 'Fighter',
    text: 'A skilled martial artist who specialises in melee combat, possessing exceptional strength, endurance, and tactical awareness in battle.',
    type: PlayerClass.Fighter,
    healthMax: 10,
    rerollMax: 2,
    dice1: [DieType.Axe, DieType.Longsword, DieType.Hammer, DieType.Spear],
    dice2: [DieType.Bracer, DieType.Shield, DieType.Helmet, DieType.Net],
    dice3: [DieType.Resilience, DieType.Strength, DieType.Speed, DieType.Reflex],
    archetypes: [
      {
        title: 'Barbarian',
        text: 'A savage warrior who specializes in using a two-handed weapon in combat, possessing exceptional strength, endurance, and ferocity in battle.',
        id: TraitType.Barbarian,
        complexity: PlayerComplexity.Moderate,
      },
      {
        title: 'Knight',
        text: 'A heavily armored warrior who specializes in using a sword and shield in combat, possessing exceptional strength, endurance, and tactical awareness in battle.',
        id: TraitType.Knight,
        complexity: PlayerComplexity.Simple,
      },
      {
        title: 'Warrior',
        text: 'A hammer wielding warrior who specializes in using a two-handed weapon in combat, possessing exceptional strength, endurance, and ferocity in battle.',
        id: TraitType.Warrior,
        complexity: PlayerComplexity.Moderate,
      },
      {
        title: 'Gladiator',
        text: 'Armed with a net and trident, the gladiator excels in agile, long-range combat, using cunning ensnarement tactics and precision strikes to conquer opponents in the arena.',
        id: TraitType.Gladiator,
        complexity: PlayerComplexity.Complex,
      },
    ],
    colour: '#B33A2E',
  },
  {
    title: 'Rogue',
    text: 'A stealthy martial artist who specializes in using surprise attacks, quick movements, and possessing exceptional agility, cunning, and reflexes.',
    type: PlayerClass.Rogue,
    healthMax: 10,
    rerollMax: 2,
    dice1: [DieType.Shivs, DieType.Dagger, DieType.Shortsword, DieType.Javellin],
    dice2: [DieType.Flask, DieType.Cloak, DieType.Bracer, DieType.Amulet],
    dice3: [DieType.Reflex, DieType.Stealth, DieType.Talented, DieType.Intelligence],
    archetypes: [
      {
        title: 'Shadow Walker',
        text: 'A stealthy rogue assassin who thrives on the element of surprise, gaining powerful bonuses from the shadows.',
        id: TraitType.ShadowWalker,
        complexity: PlayerComplexity.Moderate,
      },
      {
        title: 'Bladestorm',
        text: 'An agile assassin known for their incredible speed and precision, unleashing a relentless storm of deadly daggers upon their foes.',
        id: TraitType.Bladestorm,
        complexity: PlayerComplexity.Simple,
      },
      {
        title: 'Fencer',
        text: 'A master of the sword, adept at one-on-one combat with unparalleled finesse and effectiveness.',
        id: TraitType.Fencer,
        complexity: PlayerComplexity.Moderate,
      },
      {
        title: 'Spell Slinger',
        text: 'A novice of elemental magic, skilled in weapon infusion and ranged attacks, harnessing the elements for tactical advantages in combat.',
        id: TraitType.SpellSlinger,
        complexity: PlayerComplexity.Complex,
      },
    ],
    colour: '#3a8843',
  },
  {
    title: 'Elementalist',
    text: 'A spellcaster who has mastered the art of manipulating the elements to unleash powerful spells and abilities in battle, using their magical prowess to control and shape the battlefield to their advantage.',
    type: PlayerClass.Elementalist,
    healthMax: 10,
    rerollMax: 2,
    dice1: [DieType.Wand, DieType.Crystal, DieType.Staff, DieType.Tome],
    dice2: [DieType.Bracer, DieType.Circlet, DieType.Amulet, DieType.Earth],
    dice3: [DieType.Intelligence, DieType.Mana, DieType.Arcana, DieType.Resilience],
    archetypes: [
      {
        title: 'Anarchist',
        text: 'A volatile elemental spell caster, harnessing the fury of the elements, and embracing self-inflicted elemental harm for extraordinary power.',
        id: TraitType.Anarchist,
        complexity: PlayerComplexity.Moderate,
      },
      {
        title: 'Mana Born',
        text: 'A mage who taps into the essence of mana, accumulating power over multiple spell casts to unleash devastating magic.',
        id: TraitType.ManaBorn,
        complexity: PlayerComplexity.Simple,
      },
      {
        title: 'Wielder',
        text: 'Master of the core elements, with the rare ability to blend different elemental forces, forging destructive and unpredictable spell combinations.',
        id: TraitType.Wielder,
        complexity: PlayerComplexity.Complex,
      },
      {
        title: 'Dryad',
        text: 'A mage whose connection to the earth fuses elemental power with their skin, providing a unique blend of physical and magical defense through time-woven spells.',
        id: TraitType.Dryad,
        complexity: PlayerComplexity.Simple,
      },
    ],
    colour: '#1F3C58',
  },
]; /* .sort((a,b) => {
  if (a.title < b.title) {
    return -1;
  }
  if (a.title > b.title) {
    return 1;
  }
  return 0;
}); */
